import * as path from 'path-browserify';
import { IFileInfo, ImageFile, RawFileInfo } from './file-info';
import { Rectangle, Polygon } from './plotting/region';

export enum ProcessType {
  CROP = 'CROP',
  FIND = 'FIND',
  HTQUANT = 'HTQUANT',
  SIFT = 'SIFT',
  EDOF = 'EDOF',
  KIDNEY = 'KIDNEY_CLASSIFIER',
  DECONVOLUTION = 'DECONVOLUTION',
  YOLOSEGDETECT = 'YOLO_SEGDETECT',
  RETINAL_LAYER = 'RETINAL_LAYER',
  STAIN_ADAPTER = 'STAIN_ADAPTER',
  UNKNOWN = 'UNKNOWN',
}

export function getProcessType(type: string): ProcessType {
  const xProcessType: ProcessType | undefined = (<any>ProcessType)[type];
  if (xProcessType != undefined) {
    return xProcessType;
  } else {
    return ProcessType.UNKNOWN;
  }
}

export class Processor {
  name!: string;
  icon!: string;
  desc!: string;
  id!: ProcessType;
}

export interface Request {
  processType: ProcessType;
}

export class QuantRequest implements Request {
  processType = ProcessType.HTQUANT;
  outputName: string | null = null;
  regions: QuantRegion[] = [];
  regionSeries = 0;
  fiberSettings = new ParticleSettings();
  nucleiSettings = new ParticleSettings();
  contrastProperties = new ContrastProperties();
}

export class ParticleSettings {
  min = 0;
  max = Number.MAX_VALUE;
  cMin = 0.0;
  cMax = 1.0;
  hardBounds = true;
}

export class ContrastProperties {
  stretch = true;
  saturated = 1.0;
  normalize = true;
  gammaCorrection = false;
  // Range 0.05 - 5
  gamma = 1.0;
}

export class QuantRegion {
  name?: string;
  fileName?: string;
  polygon?: Polygon;
}

/**
 *
 * Eventually this will be a large class to hold the options for
 * cropping. For now it has only to zip or not to zip.
 */
export class SiftRequest implements Request {
  processType = ProcessType.SIFT;

  /**
   * Path in bucket to file we must align
   */
  align?: StorageKey;

  /**
   * Path in bucket to reference file
   */
  reference?: StorageKey;

  /**
   * Path in bucket to where the sift output should be written.
   */
  siftOutput?: StorageKey;

  /**
   * Path in bucket to where the shg results are.
   */
  shg?: StorageKey;

  /**
   * Whether Alignment image will be transformed or SHG/FL. True means img1 will get transformed or img3.
   * True values are y, yes, t, true, on and 1; false values are n, no, f, false, off and 0. Raises
   * ValueError if val is anything else.
   */
  transform?: boolean;

  /**
   * Vertical flip yes or no
   */
  flip?: boolean;

  /**
   * Will downsample the images if total pixel is greater than 9e7
   */
  downsample?: boolean;

  /**
   * Gamma correction value for overlay
   */
  gamma?: number;

  /**
   * Resized NZ output file in case of downsample
   */
  resizedN?: StorageKey;

  /**
   * Resized SHG/FL output file in case of downsample
   */
  resizedSF?: StorageKey;

  /**
   * Path in bucket to where the overlayed result
   * should be written.
   */
  overlayedResult?: StorageKey;
}

/**
 *
 * Eventually this will be a large class to hold the options for
 * cropping. For now it has only to zip or not to zip.
 */
export class EDoFRequest implements Request {
  processType = ProcessType.EDOF;

  /**
   * Path in bucket to file we must align
   */
  input: StorageKey[] = [];

  /**
   * Path in bucket to reference file
   */
  outputImage?: StorageKey;

  /**
   * output zmap name.
   */
  outputZmap?: StorageKey;

  /**
   * TODO Path in bucket to where the shg results are.
   */
  inputZmap?: StorageKey;

  /**
   -gradient_kernel=(5)
   Size of kernel used for computation of image gradients.  Must be 1, 3, 5, 7.
   **/
  gradientKernel?: number;

  /**
   -noise_filter=(3)
   Size of median filter used to reduce noise in the z-map. Must be 0, 3 or 5
   **/
  noiseFilter?: number;

  /**
   -low_pass=(2.000000)
   zmap lowpass filter object size.
   **/
  lowPass?: number;

}

/**
 *
 * Eventually this will be a large class to hold the options for
 * cropping. For now it has only to zip or not to zip.
 */
export class KidneyClassifierRequest implements Request {
  processType = ProcessType.KIDNEY;

  /**
   * Name of directory where raw classifier scores are saved.
   */
  bucket_name?: string | undefined = undefined; //'jax-cimg-sample-data';

  /**
   * Name of directory where raw classifier scores are saved.
   */
  output_bucket_name?: string = 'kidney_classifier_output';

  /**
   * Name of directory where images are stored.
   */
  stitched_image_dir?: string; // StorageKey

  /**
   * Name of directory where model is saved.
   */
  model_name?: string;

  /**
   * Name of directory where raw classifier scores are saved.
   */
  output_score_path?: string = 'SAVED_SCORES/';

  /**
   * Name of directory where processed classifier scores are saved.
   */
  combined_score_path?: string = 'COMBINED_AGE_SCORES/';

  /**
   * Name of directory where tissue masks are saved.
   */
  tissue_mask_path?: string;

  /**
   * Name of directory where csvs are saved.
   */
  damage_score_csv_path?: string;

  /**
   * Name of directory where heatmaps are saved.
   */
  heatmap_path?: string;

  /**
   * Lower bound used in painting algorithm.
   */
  lower_bound?: number = -4;

  /**
   * Upper bound used in painting algorithm.
   */
  upper_bound?: number = 4;

}

export class DeconvolutionRequest implements Request {

  processType = ProcessType.DECONVOLUTION;

  inputPath?: StorageKey;
  stain1?: number[];
  stain2?: number[];
  stain1Max = 2.0;
  stain2Max = 1.0;
  alpha = 1;
  beta = 0.15;
  intensityNorm = 240;
  grayscale = false;

  stain1ImageOutput?: StorageKey;
  stain2ImageOutput?: StorageKey;
  normalizedImageOutput?: StorageKey;
  imageStackOutput?: StorageKey;

  /**
   * @return the inputImage
   */
  getInputPath() {
    return this.inputPath;
  }

  /**
   * @param inputPath the inputImage to set
   */
  setInputPath(inputPath: StorageKey) {
    this.inputPath = inputPath;
  }

  /**
   * @return the stain1
   */
  getStain1() {
    return this.stain1;
  }

  /**
   * @param stain1 the stain1 to set
   */
  setStain1(stain1: number[]){
    this.stain1 = stain1;
  }

  /**
   * @return the stain2
   */
  getStain2() {
    return this.stain2;
  }

  /**
   * @param stain2 the stain2 to set
   */
  setStain2(stain2: number[]){
    this.stain2 = stain2;
  }

  /**
   * @return the stain1Max
   */
  getStain1Max() {
    return this.stain1Max;
  }

  /**
   * @param stain1Max the stain1Max to set
   */
  setStain1Max(stain1Max: number){
    this.stain1Max = stain1Max;
  }

  /**
   * @return the stain2Max
   */
  getStain2Max(){
    return this.stain2Max;
  }

  /**
   * @param stain2Max the stain2Max to set
   */
  setStain2Max(stain2Max: number){
    this.stain2Max = stain2Max;
  }

  /**
   * @return the alpha
   */
  getAlpha(){
    return this.alpha;
  }

  /**
   * @param alpha the alpha to set
   */
  setAlpha(alpha: number){
    this.alpha = alpha;
  }

  /**
   * @return the beta
   */
  getBeta(){
    return this.beta;
  }

  /**
   * @param beta the beta to set
   */
  setBeta(beta: number){
    this.beta = beta;
  }

  /**
   * @return the intensityNorm
   */
  getIntensityNorm(){
    return this.intensityNorm;
  }

  /**
   * @param intensityNorm the intensityNorm to set
   */
  setIntensityNorm(intensityNorm: number){
    this.intensityNorm = intensityNorm;
  }

  /**
   * @return the grayscale
   */
  isGrayscale(){
    return this.grayscale;
  }

  /**
   * @param grayscale the grayscale to set
   */
  setGrayscale(grayscale: boolean){
    this.grayscale = grayscale;
  }
  getStain1ImageOutput() {
    return this.stain1ImageOutput;
  }
  setStain1ImageOutput(stain1ImageOutput: StorageKey) {
    this.stain1ImageOutput = stain1ImageOutput;
  }

  getStain2ImageOutput() {
    return this.stain2ImageOutput;
  }
  setStain2ImageOutput(stain2ImageOutput: StorageKey) {
    this.stain2ImageOutput = stain2ImageOutput;
  }

  getNormalizedImageOutput() {
    return this.normalizedImageOutput;
  }
  setNormalizedImageOutput(normalizedImageOutput: StorageKey) {
    this.normalizedImageOutput = normalizedImageOutput;
  }

  getImageStackOutput() {
    return this.imageStackOutput;
  }
  setImageStackOutput(imageStackOutput: StorageKey) {
    this.imageStackOutput = imageStackOutput;
  }

}

export class YolosegdetectRequest implements Request {
  processType = ProcessType.YOLOSEGDETECT;

  inputPaths?: StorageKey[]; // StorageKey
  outputPaths?: StorageKey[]; // StorageKey
  weightsPath?: string;

  level? = 0;
  downsamplingFactor? = 5;
  overlapX? = 0;
  overlapY? = 0;
  confidence? = 0.6;
  iouThreshold? = 0.5;
  nmsThreshold? = 0.3;
  visualize? = false;
  saveSegment? = false;
}

export class StainAdapterRequest implements Request {
  processType = ProcessType.STAIN_ADAPTER;

  inputPaths?: StorageKey[]; // StorageKey
  outputPath?: StorageKey; // StorageKey

  normOnly = false;
  saveNormalization = true;
  model: string = StainAdapterModel.GR2RGB_1GRAY_2ZEROS_E23_BATCHNORM_BS1_X20; // path to model file
  mode: string  = StainAdapterMode.LAB;
  patchSize = 512; // patch size for inference, default is 512
  channelMode: string = StainAdapterChannelMode.DEFAULT; // channel mode for inference, default is DEFAULT
  normTargetPath: string = StainAdapterNormalizeTarget.SMALL_RES_VAHA_MAC
  normMethod?: string;

}

export enum StainAdapterModel {
  GR2RGB_1GRAY_2ZEROS_X20_GAMMA_AUG = 'gr2rgb-1gray-2zeros-E16-batchnorm-bs1-x20-gamma-aug.h5',
  GR2RGB_1GRAY_2ZEROS_E46_BATCHNORM_BS1 = 'gr2rgb-1gray-2zeros-E46-batchnorm-bs1.h5',
  GR2RGB_1GRAY_2ZEROS_E23_BATCHNORM_BS1_X20 = 'gr2rgb-1gray-2zeros-E23-batchnorm-bs1-x20.h5',
  GR2RGB_1GRAY_2ZEROS_E26_BATCHNORM_BS1 = 'gr2rgb-1gray-2zeros-E26-batchnorm-bs1.h5',
  GR2RGB_3GRAYS_E31_BATCHNORM_BS1 = 'gr2rgb-3grays-E31-batchnorm-bs1.h5',
  LAB_1CHANNEL_E61_V7_1_BS1_ATTN = 'lab-1channel-E61-V7-1-bs1-attn.h5',
  LAB_2L_E27_BATCHNORM_BS1 = 'lab-2L-E27-batchnorm-bs1.h5',
  LAB_L_EMPTY_E15_BATCHNORM_BS1_X20 = 'lab-l-empty-E15-batchnorm-bs1-x20.h5',
}
export enum StainAdapterMode {
  LAB = 'lab',
  GR2RGB = 'gr2rgb',
}
export enum StainAdapterNormalizeMethod {
  NONE = 'None',
  L_HISTOGRAM = 'l_histogram',
  VAHADANE = 'vahadane',
  MACENKO = 'macenko',
  REINHARD = 'reinhard',
}
export enum StainAdapterChannelMode {
  DEFAULT = 'default',
  DOUBLE_L = 'double_l',
  TRIPLE_GRAY = 'triple_gray',
  GR_2EMPTY = 'gr_2empty',
  L_EMPTY = 'l_empty',
}

export enum StainAdapterNormalizeTarget {
  SMALL_RES_VAHA_MAC = 'target_generalres_for_vahmac_smallres_for_lhistrein.tif',
  SMALL_RES_VAHA_MAC_DARKER = 'target_generalres_for_vahmac_smallres_for_lhistrein_darker.tif',
  FULL_RES_L_HISTO_EYEBALL = 'target_lhistReinhard_fullres_eyeball.tiff'
}

export class RetinalLayerRequest implements Request {
  processType = ProcessType.RETINAL_LAYER;

  images: StorageKey[] = []; // StorageKey
  predModel?: string; // StorageKey
  model: string | undefined = 'resunet_a_2d'; // can be resunet_a_2d or vnet_2d
  step: number | undefined = 512;
  patchSize: number | undefined = 512; // patch size for inference, default is 512

  outputPath?: StorageKey; // StorageKey

}

export enum RetinalLayerModels {
  RESUNET_A_2D_RL_40X = 'resunet-a-2d-RL-40x.keras',
  RESUNET_A_2D_RL_20X = 'resunet-a-2d-RL-20x.keras',
  RESUNET_A_2D_RL_40X_OLD = 'resunet-a-2d-RL-40x-old.keras',
  VNET_2D_RL_40X = 'vnet-2d-RL-40x.keras',
}

export class CropRequest implements Request {
  processType = ProcessType.CROP;
  regionSettings: RegionSettings = new RegionSettings();
  tileConfiguration: TileConfiguration = new TileConfiguration();
  cropResolution = 0;
}

export class RegionSettings {
  minimumAspectRatio = 0.15;
  margin = 10;
  findRegions = true;
  cropFoundRegionsAsRectangles = true;
  cropAsGreyScale = false;
  showRegions = false;
  generateProofImage = true;
  generatePreviews = true;
  saveAsJson = true;
  zip = true;
  manualRegions?: Polygon[] = [];
  manualRegionResolution = 0;
  forceSquare = false;
}

export class TileConfiguration {
  tileSize: number[] = [4096, 4096];

  // IMAGE: Left to right, top to bottom
  // GRAPH: Left to right, bottom to top
  order = 'IMAGE';
  clip = true;
  alwaysTile = false; // true forces tile even if image fits in memory.
}

export class Instruction {
  file!: RawFileInfo;
  request!: Request;
}

export class StorageKey {
  endpoint?: string
  bucket?: string
  object?: string
}

export class Builder  {
  bucket?: string;
  path?: string;

  keyForFile(file?: IFileInfo) : StorageKey | undefined {
    if (file === undefined) return undefined;
    return this.keyForName(file.name);
  }
  keyForName(name: string) : StorageKey | undefined {
    if (name === undefined) return undefined;
    const ret: StorageKey = new StorageKey();
    ret.bucket = this.bucket;
    if (this.path) {
      ret.object = path.join(this.path, name);
    } else {
      ret.object = name;
    }

    return ret;
  }

  getFilePathWithoutExtension(path: string) {
    const lastDotIndex = path.lastIndexOf('.');
    let imgNoExtension = path;
    if (lastDotIndex !== -1) {
      imgNoExtension = path.slice(0, lastDotIndex);
    }
    return imgNoExtension;
  }
}

export class SiftRequestBuilder {
  bucket?: string;
  path!: string;

  // inputs
  alignFile!: ImageFile;
  referenceFile!: ImageFile;
  shgFile!: ImageFile;
  transform = true;
  downsample = false;
  flip = true;
  gamma = 1;

  // outputs
  siftOutputName?: string = 'translated.tif';
  overlayedResultName?: string = 'overlayed.tif';
  resizedNzName = 'resized_NZ.tif';
  resizedSfName = 'resized_SF.tif';
  outputFolder = '';

  build(): SiftRequest {
    const siftRequest: SiftRequest = new SiftRequest();
    siftRequest.align = this.key(this.alignFile.name);
    siftRequest.reference = this.key(this.referenceFile.name);
    siftRequest.shg = this.key(this.shgFile.name);
    siftRequest.transform = this.transform;
    siftRequest.flip = this.flip;
    siftRequest.gamma = this.gamma;
    siftRequest.downsample = this.downsample;
    siftRequest.siftOutput = this.key(this.outputFolder + '/' + this.siftOutputName);
    siftRequest.overlayedResult = this.key(this.outputFolder + '/' + this.overlayedResultName);
    siftRequest.resizedN = this.key(this.outputFolder + '/' + this.resizedNzName);
    siftRequest.resizedSF = this.key(this.outputFolder + '/' + this.resizedSfName);
    return siftRequest;
  }

  key(name: any) : StorageKey {
    const ret: StorageKey = new StorageKey();
    ret.bucket = this.bucket;
    ret.object = this.path.concat(name);
    return ret;
  }

}

export class EDoFRequestBuilder extends Builder {
  // inputs
  input?: IFileInfo;
  inputZmap?: IFileInfo;
  gradientKernel?: number = 5;
  noiseFilter?: number = 3;
  lowPass?: number = 4.0;

  // outputs
  outputFolder = '';
  outputImage = 'extended_depth.tif';
  outputZmap = 'zmap.tif';

  build(): EDoFRequest {
    const edofRequest: EDoFRequest = new EDoFRequest();
    if (this.input) {
      const input: StorageKey | undefined = this.keyForFile(this.input);
      if (input) {
        // TODO This only works for image stack single files. We may also have stack as directory.
        edofRequest.input.push(input);
      }
    }
    edofRequest.outputImage = this.keyForName(this.outputFolder + '/' + this.outputImage);

    edofRequest.outputZmap = this.keyForName(this.outputFolder + '/' + this.outputZmap);

    if (this.inputZmap) {
      edofRequest.inputZmap = this.keyForFile(this.inputZmap);
    }
    edofRequest.gradientKernel = this.gradientKernel;
    edofRequest.noiseFilter = this.noiseFilter;
    edofRequest.lowPass = this.lowPass;
    return edofRequest;
  }

}

export class YolosegdetectRequestBuilder extends Builder {
  // inputs
  inputImages?: IFileInfo[];
  modelFilePath?: string;
  modelFile?: IFileInfo;
  level?: number;
  downSamplingFactor?: number;
  overlapX?: number;
  overlapY?: number;
  confidence?: number;
  iouThreshold?: number;
  nmsThreshold?: number;
  saveSegment?: boolean;
  // outputs
  outputFolder = '';

  build(): YolosegdetectRequest {
    const yoloSegDetectRequest: YolosegdetectRequest = new YolosegdetectRequest();
    if (this.inputImages) {
      yoloSegDetectRequest.inputPaths = [];
      yoloSegDetectRequest.outputPaths = [];
      for (const image of this.inputImages) {
        const imageInput: StorageKey | undefined = this.keyForFile(image);
        if (imageInput) {
          yoloSegDetectRequest.inputPaths.push(imageInput);
          // set output
          let outputPath = undefined;
          if (this.outputFolder) {
            outputPath = this.keyForName(this.outputFolder + '/' + this.getFilePathWithoutExtension(image.name) + '.geojson');
          } else {
            outputPath = this.keyForName(this.getFilePathWithoutExtension(image.name) + '.geojson');
          }
          if (outputPath)
            yoloSegDetectRequest.outputPaths.push(outputPath);
        }
      }
    }
    // get StorageKey from modelfile path
    if (this.modelFilePath) {
      const bucket = this.modelFilePath.split('/')[0];
      const object = this.modelFilePath.split('/').slice(1).join('/');
      const modelFile: StorageKey = new StorageKey();
      modelFile.bucket = bucket;
      modelFile.object = object;
      yoloSegDetectRequest.weightsPath = this.modelFilePath;
    }
    yoloSegDetectRequest.level = this.level;
    yoloSegDetectRequest.downsamplingFactor = this.downSamplingFactor;
    yoloSegDetectRequest.overlapX = this.overlapX;
    yoloSegDetectRequest.overlapY = this.overlapY;
    yoloSegDetectRequest.confidence = this.confidence;
    yoloSegDetectRequest.iouThreshold = this.iouThreshold;
    yoloSegDetectRequest.nmsThreshold = this.nmsThreshold;
    yoloSegDetectRequest.saveSegment = this.saveSegment;
    return yoloSegDetectRequest;
  }
}

export class RetinalLayerRequestBuilder extends Builder {
  // inputs
  images?: IFileInfo[];
  predModel?: string;
  model?: string;
  step?: number;
  patchSize?: number;

  // outputs
  outputPath!: string;

  build(): RetinalLayerRequest {
    const retinalLayerRequest: RetinalLayerRequest = new RetinalLayerRequest();
    let outputPath = undefined;

    if (this.images) {
      retinalLayerRequest.images = [];
      if (this.images.length > 1) {
        // set output with name of directory where images are located
        if (this.outputPath) {
          outputPath = this.keyForName(path.join(this.outputPath, path.dirname(this.images[0].name) + '_results'));
        } else {
          outputPath = this.keyForName(path.dirname(this.images[0].name) + '_results');
        }
      } else {
        // set output with name of image
        if (this.outputPath) {
          outputPath = this.keyForName(path.join(this.outputPath, path.basename(this.images[0].name, path.extname(this.images[0].name)) + '_results'));
        } else {
          outputPath = this.keyForName(path.basename(this.images[0].name, path.extname(this.images[0].name)));
        }
      }
      for (const image of this.images) {
        const imageInput: StorageKey | undefined = this.keyForFile(image);
        if (imageInput) {
          retinalLayerRequest.images.push(imageInput);
        }
      }
    }

    retinalLayerRequest.outputPath = outputPath;
    if (this.predModel) {
      retinalLayerRequest.predModel = this.predModel;
    } else {
      throw new Error('predModel is required');
    }
    retinalLayerRequest.step = this.step;
    retinalLayerRequest.patchSize = this.patchSize;
    retinalLayerRequest.model = this.model;
    return retinalLayerRequest;
  }

}

export class StainAdapterRequestBuilder extends Builder {
  // inputs
  inputImages?: IFileInfo[];

  normOnly = false;
  saveNormalization = true;
  normMethod: string | undefined;
  mode: string = StainAdapterMode.LAB;
  patchSize = 512;
  channelMode: string = StainAdapterChannelMode.DEFAULT;
  ganModelPath: string = StainAdapterModel.GR2RGB_1GRAY_2ZEROS_E23_BATCHNORM_BS1_X20;
  normTargetPath: string = StainAdapterNormalizeTarget.SMALL_RES_VAHA_MAC;
  // outputs
  outputFolder = '';

  build(): StainAdapterRequest {
    const stainAdapterRequest: StainAdapterRequest = new StainAdapterRequest();

    if (this.inputImages) {
      stainAdapterRequest.inputPaths = [];
      for (const image of this.inputImages) {
        const imageInput = this.keyForFile(image);
        if (imageInput) {
          stainAdapterRequest.outputPath = this.keyForName(this.outputFolder);
          stainAdapterRequest.inputPaths?.push(imageInput);
        }
      }
      stainAdapterRequest.normOnly = this.normOnly;
      stainAdapterRequest.mode = this.mode;
      stainAdapterRequest.patchSize = this.patchSize;
      stainAdapterRequest.channelMode = this.channelMode;
      stainAdapterRequest.saveNormalization = this.saveNormalization;
      stainAdapterRequest.model = '/app/models/' + this.ganModelPath;
      stainAdapterRequest.normTargetPath = '/app/normalization-targets/' + this.normTargetPath;
      stainAdapterRequest.normMethod = this.normMethod;
    }

    return stainAdapterRequest;
  }
}

export class KidneyClassifierRequestBuilder {
  bucket?: string;
  path!: string;

  imageDirectory!: ImageFile;
  modelFile!: string;

  // outputs
  heatmapPath?: string = 'HEAT_MAPS/';
  damageScorePath?: string = 'DAMAGE_SCORE_CSVS/';
  tissueMaskPath?: string = 'TISSUE_MASKS/'

  build(): KidneyClassifierRequest {
    const kidneyClassifierRequest: KidneyClassifierRequest = new KidneyClassifierRequest();
    const basePath = this.path.concat(this.imageDirectory.name);
    kidneyClassifierRequest.stitched_image_dir = basePath.endsWith('/') ? basePath : basePath + '/';
    kidneyClassifierRequest.model_name = this.modelFile;
    kidneyClassifierRequest.output_bucket_name = this.bucket;
    kidneyClassifierRequest.tissue_mask_path = this.tissueMaskPath;
    kidneyClassifierRequest.damage_score_csv_path = this.damageScorePath;
    kidneyClassifierRequest.heatmap_path = this.heatmapPath;
    return kidneyClassifierRequest;
  }

  key(name: any) : StorageKey {
    const ret: StorageKey = new StorageKey();
    ret.bucket = this.bucket;
    ret.object = this.path.concat(name);
    return ret;
  }

}

export class DeconvolutionBuilder extends Builder {
  // inputs
  inputImage!: IFileInfo;
  red1!: number;
  green1!: number;
  blue1!: number;
  red2!: number;
  green2!: number;
  blue2!: number;
  stain1max!: number;
  stain2max!: number;
  alpha!: number;
  beta!:number;
  intensityNorm!: number;
  grayscale!: boolean;

  // outputs
  outputPath!: string ;

  build(): DeconvolutionRequest {
    const deconvolutionRequest: DeconvolutionRequest = new DeconvolutionRequest();
    deconvolutionRequest.inputPath = this.keyForFile(this.inputImage);
    console.log('inputpath in builder:', deconvolutionRequest.inputPath);
    deconvolutionRequest.stain1 = [this.red1, this.green1, this.blue1];
    deconvolutionRequest.stain2 = [this.red2, this.green2, this.blue2];
    deconvolutionRequest.stain1Max = this.stain1max;
    deconvolutionRequest.stain2Max = this.stain2max;
    deconvolutionRequest.alpha = this.alpha;
    deconvolutionRequest.beta = this.beta;
    deconvolutionRequest.intensityNorm = this.intensityNorm;
    deconvolutionRequest.grayscale = this.grayscale;
    if (this.outputPath) {
      deconvolutionRequest.stain1ImageOutput = this.keyForName(this.outputPath + '/' + this.inputImage.name.split('.')[0] + '_stain1.tif');
      deconvolutionRequest.stain2ImageOutput = this.keyForName(this.outputPath + '/' + this.inputImage.name.split('.')[0] + '_stain2.tif');
      deconvolutionRequest.normalizedImageOutput = this.keyForName(this.outputPath + '/' + this.inputImage.name.split('.')[0] + '_normalized.tif');
      deconvolutionRequest.imageStackOutput = this.keyForName(this.outputPath + '/' + this.inputImage.name.split('.')[0] + '_stack.tif');
    } else {
      deconvolutionRequest.stain1ImageOutput = this.keyForName(this.inputImage.name.split('.')[0] + '_stain1.tif');
      deconvolutionRequest.stain2ImageOutput = this.keyForName(this.inputImage.name.split('.')[0] + '_stain2.tif');
      deconvolutionRequest.normalizedImageOutput = this.keyForName(this.inputImage.name.split('.')[0] + '_normalized.tif');
      deconvolutionRequest.imageStackOutput = this.keyForName(this.inputImage.name.split('.')[0] + '_stack.tif');
    }
    return deconvolutionRequest;
  }
}

export enum ValidationType {
  VALID = 'VALID',
  LIKELY_VALID = 'LIKELY_VALID',
	LIKELY_INVALID = 'LIKELY_INVALID',
  INVALID = 'INVALID',
}

export interface Validation {
  type: string,
  message: string,
}

export class ZoomRequest {
  info!: RawFileInfo;
  key!: string;
  roi!: Rectangle;
  zIndex!: number;
  screen!: Rectangle;
  resolution!: number;
}


